/**
 * Render the job listings into the DOM
 *
 * @package Interact_26
 */

import { type PinpointJob, type CareersConfig } from './types';
import { getMappedDepartment, escapeHtml, escapeAttr } from './utils';

const CHEVRON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M13.1717 12.0007L8.22192 7.05093L9.63614 5.63672L16.0001 12.0007L9.63614 18.3646L8.22192 16.9504L13.1717 12.0007Z"/></svg>`;

/**
 * Map location keywords to flag SVG filenames.
 */
const LOCATION_FLAG_MAP: Record<string, string> = {
  uk: 'united-kingdom.svg',
  'united kingdom': 'united-kingdom.svg',
  england: 'united-kingdom.svg',
  manchester: 'united-kingdom.svg',
  us: 'united-states.svg',
  usa: 'united-states.svg',
  'united states': 'united-states.svg',
  ok: 'united-states.svg',
  tulsa: 'united-states.svg',
  ny: 'united-states.svg',
  'new york': 'united-states.svg',
  ph: 'philippines.svg',
  philippines: 'philippines.svg',
  manila: 'philippines.svg',
  pl: 'poland.svg',
  poland: 'poland.svg',
  warsaw: 'poland.svg',
  ca: 'canada.svg',
  canada: 'canada.svg',
  uae: 'uaeflag.svg',
  dubai: 'uaeflag.svg',
};

/**
 * Get the flag SVG filename for a location like "Manchester, UK" or
 * "UK - Manchester Office". An unknown country returns an empty string and
 * the row simply renders without a flag.
 */
const getLocationFlagFile = (location: string): string => {
  if (!location) return '';

  const lower = location.toLowerCase();

  // Whole-word match only: a substring scan gave "Australia" the US flag.
  for (const [key, file] of Object.entries(LOCATION_FLAG_MAP)) {
    if (new RegExp(`(^|[^a-z])${key}($|[^a-z])`).test(lower)) return file;
  }

  return '';
};

/**
 * Render a loading skeleton into the container.
 */
export const renderJobsLoading = (container: HTMLElement): void => {
  const skeletonCount = 5;
  let html = '<div class="careers-jobs__skeleton">';

  for (let i = 0; i < skeletonCount; i++) {
    html += `
      <div class="careers-jobs__skeleton-item">
        <div class="careers-jobs__skeleton-bar careers-jobs__skeleton-bar--title"></div>
        <div class="careers-jobs__skeleton-bar careers-jobs__skeleton-bar--badge"></div>
        <div class="careers-jobs__skeleton-bar careers-jobs__skeleton-bar--meta"></div>
        <div class="careers-jobs__skeleton-bar careers-jobs__skeleton-bar--location"></div>
      </div>`;
  }

  html += '</div>';
  container.innerHTML = html;
};

/**
 * Render the list of jobs into the target container.
 */
export const renderJobsList = (
  jobs: PinpointJob[],
  container: HTMLElement,
  config: CareersConfig,
  onJobClick: (jobId: string) => void,
): void => {
  if (jobs.length === 0) {
    const message = config.departmentFilter
      ? config.i18n.noOpenPositionsDept
      : config.i18n.noOpenPositions;

    container.innerHTML = `
      <div class="careers-jobs__empty">
        <p class="typography typo-body typo-medium typo-font-body">${escapeHtml(message)}</p>
      </div>`;
    return;
  }

  const fragment = document.createDocumentFragment();

  for (const job of jobs) {
    const department = getMappedDepartment(job.department, config);
    const flagFile = getLocationFlagFile(job.location);
    const flagHtml =
      flagFile && config.flagsUrl
        ? `<img src="${escapeAttr(config.flagsUrl + '/' + flagFile)}" alt="" class="careers-jobs__item-flag" width="24" height="16" aria-hidden="true">`
        : '';

    const row = document.createElement('div');
    row.className = 'careers-jobs__item';
    row.setAttribute('role', 'listitem');
    row.setAttribute('tabindex', '0');
    row.setAttribute('aria-label', `${job.title} — ${job.location}`);
    row.setAttribute('data-job-id', job.id);

    row.innerHTML = `
      <span class="careers-jobs__item-title">${escapeHtml(job.title)}</span>
      <span class="careers-jobs__item-department">${escapeHtml(department.toUpperCase())}</span>
      <span class="careers-jobs__item-type">${escapeHtml(job.employment_type)}</span>
      <span class="careers-jobs__item-location">${flagHtml}${escapeHtml(job.location)}</span>
      <span class="careers-jobs__item-chevron" aria-hidden="true">${CHEVRON_SVG}</span>`;

    const handleClick = () => onJobClick(job.id);

    row.addEventListener('click', handleClick);
    row.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    });

    fragment.appendChild(row);
  }

  container.innerHTML = '';
  container.appendChild(fragment);
};

/**
 * Render an error message.
 */
export const renderJobsError = (container: HTMLElement, config: CareersConfig): void => {
  container.innerHTML = `
    <div class="careers-jobs__empty">
      <p class="typography typo-body typo-medium typo-font-body">${escapeHtml(config.i18n.errorLoading)}</p>
    </div>`;
};
